
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { WhiteLabelConfig } from '../types';
import { db, auth } from '../services/firebase';
import { doc, getDoc, setDoc } from 'firebase/firestore';

// ─── TYPES ────────────────────────────────────────────────────────────────────

export interface BrandConfig {
  name: string;           // "Aura Traveler" or agency name
  logo?: string;          // Logo URL
  primaryColor: string;   // Gold or custom
  accentColor?: string;   // Secondary accent
  tagline: string;
  contactEmail?: string;
  contactPhone?: string;
  website: string;
  footerText: string;
  isWhiteLabel: boolean;  // Whether white-label is active
}

interface BrandContextValue {
  brand: BrandConfig;
  whiteLabelConfig: WhiteLabelConfig | null;
  updateWhiteLabel: (config: WhiteLabelConfig) => Promise<void>;
  resetToDefaults: () => Promise<void>;
  isLoading: boolean;
}

// ─── DEFAULTS ─────────────────────────────────────────────────────────────────

export const defaultBrand: BrandConfig = {
  name: 'Aura Traveler',
  primaryColor: '#D4AF37',
  tagline: 'Architecture of the Voyage',
  website: 'auratraveler.com',
  footerText: 'Crafted with Aura Traveler AI',
  isWhiteLabel: false,
};

// ─── CONTEXT ──────────────────────────────────────────────────────────────────

const BrandContext = createContext<BrandContextValue | null>(null);

// ─── HELPER: Strip undefined/null values before writing to Firestore ─────────

const sanitizeForFirestore = (obj: any): any => {
  if (Array.isArray(obj)) {
    return obj.map(v => sanitizeForFirestore(v));
  } else if (obj !== null && typeof obj === 'object') {
    return Object.fromEntries(
      Object.entries(obj)
        .filter(([_, v]) => v !== undefined && v !== null)
        .map(([k, v]) => [k, sanitizeForFirestore(v)])
    );
  }
  return obj;
};

// ─── HELPER: Merge white-label config with defaults ───────────────────────────

function buildBrandConfig(wlConfig: WhiteLabelConfig | null): BrandConfig {
  if (!wlConfig || !wlConfig.enabled) {
    return { ...defaultBrand };
  }

  return {
    name: wlConfig.agencyName || defaultBrand.name,
    logo: wlConfig.agencyLogo,
    primaryColor: wlConfig.primaryColor || defaultBrand.primaryColor,
    accentColor: wlConfig.accentColor,
    tagline: wlConfig.tagline || defaultBrand.tagline,
    contactEmail: wlConfig.contactEmail,
    contactPhone: wlConfig.contactPhone,
    website: wlConfig.website || defaultBrand.website,
    footerText: wlConfig.footerText || `Crafted with ${wlConfig.agencyName || defaultBrand.name}`,
    isWhiteLabel: true,
  };
}

// ─── PROVIDER ─────────────────────────────────────────────────────────────────

export const BrandProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [whiteLabelConfig, setWhiteLabelConfig] = useState<WhiteLabelConfig | null>(null);
  const [brand, setBrand] = useState<BrandConfig>(defaultBrand);
  const [isLoading, setIsLoading] = useState(true);

  // Load white-label config from Firestore on auth change
  useEffect(() => {
    const loadConfig = async () => {
      if (!db || !auth?.currentUser) {
        setIsLoading(false);
        return;
      }

      try {
        const configRef = doc(db, 'users', auth.currentUser.uid, 'settings', 'whiteLabel');
        const configSnap = await getDoc(configRef);

        if (configSnap.exists()) {
          const config = configSnap.data() as WhiteLabelConfig;
          setWhiteLabelConfig(config);
          setBrand(buildBrandConfig(config));
        }
      } catch (err) {
        console.warn('[BrandContext] Could not load white-label config:', err);
      } finally {
        setIsLoading(false);
      }
    };

    loadConfig();
  }, [auth?.currentUser?.uid]);

  // Inject CSS custom property when brand color changes
  useEffect(() => {
    if (brand.primaryColor !== '#D4AF37') {
      document.documentElement.style.setProperty('--color-gold', brand.primaryColor);
      if (brand.accentColor) {
        document.documentElement.style.setProperty('--color-accent', brand.accentColor);
      }
    }
    return () => {
      document.documentElement.style.removeProperty('--color-gold');
      document.documentElement.style.removeProperty('--color-accent');
    };
  }, [brand.primaryColor, brand.accentColor]);

  const updateWhiteLabel = useCallback(async (config: WhiteLabelConfig) => {
    if (!db || !auth?.currentUser) return;

    try {
      const configRef = doc(db, 'users', auth.currentUser.uid, 'settings', 'whiteLabel');
      await setDoc(configRef, sanitizeForFirestore(config));
      setWhiteLabelConfig(config);
      setBrand(buildBrandConfig(config));
    } catch (err) {
      console.error('[BrandContext] Failed to save white-label config:', err);
      throw err;
    }
  }, []);

  const resetToDefaults = useCallback(async () => {
    if (!db || !auth?.currentUser) return;

    try {
      const emptyConfig: WhiteLabelConfig = {
        enabled: false,
        agencyName: '',
      };
      const configRef = doc(db, 'users', auth.currentUser.uid, 'settings', 'whiteLabel');
      await setDoc(configRef, sanitizeForFirestore(emptyConfig));
      setWhiteLabelConfig(null);
      setBrand({ ...defaultBrand });
    } catch (err) {
      console.error('[BrandContext] Failed to reset white-label config:', err);
      throw err;
    }
  }, []);

  return (
    <BrandContext.Provider value={{ brand, whiteLabelConfig, updateWhiteLabel, resetToDefaults, isLoading }}>
      {children}
    </BrandContext.Provider>
  );
};

// ─── HOOK ─────────────────────────────────────────────────────────────────────

export const useBrand = (): BrandContextValue => {
  const context = useContext(BrandContext);
  if (!context) {
    // Return defaults when used outside provider (e.g., public itinerary pages)
    return {
      brand: defaultBrand,
      whiteLabelConfig: null,
      updateWhiteLabel: async () => {},
      resetToDefaults: async () => {},
      isLoading: false,
    };
  }
  return context;
};
